import React, {useCallback, useMemo, useState} from 'react';
import {useForm, useFormState} from 'react-final-form';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import CountryModel from 'coreClasses/countries/CountryModel';
import {useLocalesMgr} from 'coreHooks';
import validationRules, {defaultPhoneValidationRules} from 'coreUtils/validationRules';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {Dropdown} from 'brandComponents';
import Field from 'brandComponents/Form/Subcomponents/Field';
import {FieldValidation} from 'brandComponents/Form/Subcomponents/Field/types';
import Icon from 'brandComponents/Icon';
import Typography from 'brandComponents/Typography';

import MemoizedPhoneCodeOptions from './Subcomponents/MemoizedPhoneCodeOptions';
import {phoneCodes} from './config';
import {IPhoneCodeConfig, IPhoneFieldProps, IPhoneFieldTranslations} from './IPhoneField';
import messages from './messages';

// eslint-disable-next-line no-restricted-imports
import './PhoneField.scss';

const PhoneField = ({
	initialPhoneCode,
	initialPhoneNumber,
	initialCountry,
	isPhoneCodeShouldBeUpdated,
	disabled,
	classNames,
	validation,
	error,
	overrideTranslations,
	customIcon,
	label,
	placeholder,
	comment,
	showElement = {
		label: true,
		tip: true,
	},
	isRequired = true,
	maxLength,
	subscription,
	phoneCodeExtended,
	autoComplete = false,
	floatingLabel = false,
	direction,
	labelPhoneCode,
	...props
}: IPhoneFieldProps) => {
	const intl = useIntl();
	const {managerRepository} = useGeneralProvider();
	const localeMgr = useLocalesMgr();
	const formState = useFormState();
	const form = useForm();
	const currentLocale = localeMgr.getCurrentLocale();
	const countryCode = currentLocale.getCountryCode();
	const currentPhoneCode = formState.values.phoneCode;

	//UPDATE PREFIX ON COUNTRY CHANGE
	const initialCountryCodeByPhoneCode = useMemo(() =>
		phoneCodes.find(p => p.code === initialPhoneCode)?.countryCode || null,
	[initialPhoneCode, phoneCodes]);

	const selectedCountryCode = initialCountry || countryCode;
	const fieldName = props?.name || 'phone';
	const [country, setCountry] = useState(initialCountryCodeByPhoneCode || localeMgr.getCurrentLocale().getCountryCode());

	const countriesDataBuilder = (countries: CountryModel[]) => {
		const countriesArray = countries.map((country) => {
			const countryMessage = {
				id: `country.${country.getCountryCode()}`,
				defaultMessage: country.getCountryCode(),
			};

			return {
				value: country.getCountryCode(),
				label: intl.formatMessage(countryMessage),
			};
		});

		return countriesArray.sort((a, b) => a.label?.localeCompare(b?.label));
	};

	const countries = managerRepository.getCountriesMgr().getCountries();
	const countriesData = countriesDataBuilder(countries);
	const handleChangeCountry = useCallback((newValue: string) => {
		setCountry(newValue);
	}, []);

	const countryObj = useMemo(() => countriesData.find((i) => i.value === country), [country, countriesData]);

	const translations: IPhoneFieldTranslations = {
		tip: overrideTranslations?.tip || intl.formatMessage(messages.tip),
		label: overrideTranslations?.label || intl.formatMessage(messages.label),
		invalidError: overrideTranslations?.invalidError || intl.formatMessage(messages.invalidError),
		phoneCodeExtended: overrideTranslations?.phoneCodeExtended || intl.formatMessage(messages.phoneCodeExtendedLabel),
		phone: overrideTranslations?.phone || intl.formatMessage(messages.phoneLabel),
	};

	const sortedPhoneCodes = useMemo(() =>
		[...phoneCodes].sort((a: IPhoneCodeConfig, b: IPhoneCodeConfig) => a.code.localeCompare(b.code)), [phoneCodes],
	);

	//VALUE PREFIX ON COUNTRY CHANGE
	const newDefaultCountryCode = useMemo(() => {
		const find = sortedPhoneCodes.find((elem) => elem?.countryCode === countryObj?.value);

		if (typeof form.mutators.setPhoneCode === 'function') {
			form.mutators.setPhoneCode(find?.code, find?.countryCode);
		}

		return find;

	}, [sortedPhoneCodes, countryObj?.value]);

	const value = form.getFieldState(fieldName)?.value;
	const isPhoneValueFilled = value?.length > 0;

	const selectedOption: IPhoneCodeConfig = useMemo(() => {
		return (
			(selectedCountryCode && sortedPhoneCodes.find((item: IPhoneCodeConfig) =>
				item.countryCode === selectedCountryCode)
			) || sortedPhoneCodes[0]
		);
	}, [selectedCountryCode]);

	if (isPhoneCodeShouldBeUpdated) {
		form.change('phoneCode', selectedOption.code);
	}

	// Limit the phone field to "maxlength" characters if it is longer than that
	if (maxLength && value && value.length >= maxLength) {
		form.change(fieldName, value.slice(0, maxLength));
	}

	const currentPhoneValidation: FieldValidation[] = useMemo(() => {
		const phoneCodeConfig: IPhoneCodeConfig = sortedPhoneCodes.find(
			(code: IPhoneCodeConfig) => code.code === currentPhoneCode,
		);
		const validationMinLength: number = phoneCodeConfig?.validation?.minLength;
		const validationMaxLength: number = phoneCodeConfig?.validation?.maxLength;
		const regexp: RegExp = phoneCodeConfig?.validation?.regexp;
		const isSpecificPhoneCodeLengthValidation: boolean = !!(validationMaxLength && validationMinLength);
		const phoneValidation: FieldValidation[] = [];

		if (isRequired || isPhoneValueFilled) {
			phoneValidation.push({
				rule: isSpecificPhoneCodeLengthValidation
					? validationRules.hasCorrectLength(validationMinLength, validationMaxLength)
					: validationRules.hasCorrectPhoneNumberLength,
				errorMessage: intl.formatMessage(messages.lengthError, {
					minLength: 0,
					maxLength: isSpecificPhoneCodeLengthValidation
						? validationMaxLength
						: defaultPhoneValidationRules.maxLength,
				}),
			});

			phoneValidation.push({
				rule: regexp ? (value: string) => regexp.test(value) : validationRules.isPhoneNumber,
				errorMessage: translations.invalidError,
			});
		}

		if (validation) {
			phoneValidation.push(...validation);
		}

		return phoneValidation;
	}, [currentPhoneCode, isPhoneValueFilled]);

	const labelText = label || translations.label;
	const showLabel = labelText && showElement.label;
	const showFloatingLabel = floatingLabel && showLabel;

	return (
		<div className={cn('phone-field', classNames)}>
			{!showFloatingLabel && (
				<Typography variant="span" classNames="phone-label">
					{labelText}
				</Typography>
			)}
			<div className="fields-phone">
				{phoneCodeExtended && autoComplete ? (
					<div className="fields-phone-country_caller">
						<span className="phoneCode-label__extended">{translations.phoneCodeExtended}</span>
						<Dropdown
							value={countryObj?.label ?? country}
							label={countryObj?.label ?? country}
							direction={direction}
						>
							<Dropdown.Trigger
								customIcon={customIcon}
							/>
							<Dropdown.Menu
								data={countriesData}
								onSelect={handleChangeCountry}
								searchable
							/>
						</Dropdown>
					</div>
				) : null}
				<Field
					name="phoneCode"
					component="select"
					label={labelPhoneCode}
					classNames={error ? 'invalid' : null}
					initialValue={
						newDefaultCountryCode?.code === selectedOption?.code ?
							newDefaultCountryCode?.code :
							initialPhoneCode && initialPhoneCode === newDefaultCountryCode?.code ?
								initialPhoneCode :
								selectedOption?.code
					}
					customIcon={customIcon || <Icon name={'caret-down-outline'} width={9} height={5} />}
					disabled={phoneCodeExtended === false}
				>
					<MemoizedPhoneCodeOptions phoneCodes={sortedPhoneCodes} />
				</Field>

				<Field
					label={showFloatingLabel && labelText}
					name={fieldName}
					component="input"
					type="tel"
					disabled={disabled}
					initialValue={initialPhoneNumber}
					classNames={error ? 'invalid' : null}
					validation={currentPhoneValidation}
					placeholder={placeholder}
					comment={comment}
					subscription={subscription}
					showSubmitError={!(!isRequired && !value)}
					{...props}
				/>
			</div>
			{translations.tip && showElement.tip && (
				<Typography variant="span" classNames="field-comment">
					{translations.tip}
				</Typography>
			)}
		</div>
	);
};

export default PhoneField;
